/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Operations, Payload } from '@tivocorp/tivo-webapp-loggers';

import { Storage } from '../storage/Storage';

interface TAppSession {
    appVersion: string;
    state: number;
    referenceTime?: number;
    shellAppVersion?: string;
}
export class RestartReasonsOperationsLogger {
    private static AppExitReasons = {
        APP_UPGRADE: 'AppUpgrade',
        APP_UPPDATE: 'AppUpdate',
        CRASH_ALL: 'CrashAll',
        FIRST_START: 'FirstStart',
        TERMINATE_IN_BACKGROUND: 'TerminateInBackground',
        UNKNOWN: 'Unknown',
        USER_RETURN: 'UserReturn',
    };

    public static AppState = {
        NORMAL_USER_EXIT: 0,
        RUNNING: 1,
        BACKGROUND: 2,
    };

    private static mRestartReasonLogger: RestartReasonsOperationsLogger;

    private mCurrentAppSession: TAppSession | null;

    private mPreviousAppSession: TAppSession | null;

    private mRestartReason: string = RestartReasonsOperationsLogger.AppExitReasons.UNKNOWN;

    private mEventProperties = {
        EVENT_NAME: 'event_name',
        REFERENCE_TIME: 'referenceTime',
        RESTART_REASON: 'restartReason',
    };

    private constructor() {
        this.mPreviousAppSession = null;
        this.mCurrentAppSession = null;
        const storedAppState = Storage.getInstance().getItem(Storage.Keys.APP_STATE);
        if (storedAppState) this.mPreviousAppSession = { ...storedAppState };
    }

    public initialize(appVersion: string, shellAppVersion?: string, referenceTime?: number): void {
        this.mCurrentAppSession = {
            appVersion,
            shellAppVersion,
            referenceTime: new Date().getTime(),
            state: RestartReasonsOperationsLogger.AppState.RUNNING,
        };
        if (!this.mPreviousAppSession) {
            this.mRestartReason = RestartReasonsOperationsLogger.AppExitReasons.FIRST_START;
            Storage.getInstance().setItem(Storage.Keys.APP_STATE, this.mCurrentAppSession);
            return;
        }
        if (referenceTime) {
            this.mRestartReason = RestartReasonsOperationsLogger.AppExitReasons.CRASH_ALL;
            this.mPreviousAppSession.referenceTime = referenceTime;
        } else if (this.mPreviousAppSession.appVersion !== appVersion) {
            this.mRestartReason = RestartReasonsOperationsLogger.AppExitReasons.APP_UPPDATE;
        } else if (this.mPreviousAppSession.shellAppVersion !== shellAppVersion) {
            this.mRestartReason = RestartReasonsOperationsLogger.AppExitReasons.APP_UPGRADE;
        } else if (
            this.mPreviousAppSession.state ===
            RestartReasonsOperationsLogger.AppState.NORMAL_USER_EXIT
        ) {
            this.mRestartReason = RestartReasonsOperationsLogger.AppExitReasons.USER_RETURN;
        } else if (
            this.mPreviousAppSession.state === RestartReasonsOperationsLogger.AppState.BACKGROUND
        ) {
            this.mRestartReason =
                RestartReasonsOperationsLogger.AppExitReasons.TERMINATE_IN_BACKGROUND;
        } else {
            this.mRestartReason = RestartReasonsOperationsLogger.AppExitReasons.UNKNOWN;
        }
        // TODO: We need to determine some of the restart reason based on previous app session state
        Storage.getInstance().setItem(Storage.Keys.APP_STATE, this.mCurrentAppSession);
    }

    public logRestartReason(): void {
        const payload = new Payload();
        payload.add(this.mEventProperties.EVENT_NAME, 'appRestartReason');
        payload.add(this.mEventProperties.RESTART_REASON, this.mRestartReason);
        if (this.mPreviousAppSession?.referenceTime)
            payload.add(
                this.mEventProperties.REFERENCE_TIME,
                this.mPreviousAppSession?.referenceTime,
            );
        Operations.log(payload);
    }

    public updateAppState(state: number): void {
        const wasNormalExit =
            Storage.getInstance().getItem(Storage.Keys.APP_STATE)?.state ===
            RestartReasonsOperationsLogger.AppState.NORMAL_USER_EXIT;

        if (state === RestartReasonsOperationsLogger.AppState.BACKGROUND && wasNormalExit) return;
        if (this.mCurrentAppSession) {
            this.mCurrentAppSession.state = state;
            this.mCurrentAppSession.referenceTime = new Date().getTime();
            Storage.getInstance().setItem(Storage.Keys.APP_STATE, this.mCurrentAppSession);
        }
    }

    public static getInstance(): RestartReasonsOperationsLogger {
        if (!RestartReasonsOperationsLogger.mRestartReasonLogger) {
            RestartReasonsOperationsLogger.mRestartReasonLogger =
                new RestartReasonsOperationsLogger();
        }
        return RestartReasonsOperationsLogger.mRestartReasonLogger;
    }
}
